const uniqueIds = (ids: string[]): string[] => {
  const seen = new Set<string>()
  const output: string[] = []
  ids.forEach(id => {
    if (!id || seen.has(id)) {
      return
    }
    seen.add(id)
    output.push(id)
  })
  return output
}

export const sanitizeHiddenWitnessIds = (
  orderedWitnessIds: string[],
  hiddenWitnessIds: string[],
  lockVisibleWitnessIds: string[] = []
): string[] => {
  const orderedSet = new Set(orderedWitnessIds)
  const lockedSet = new Set(lockVisibleWitnessIds)
  const hiddenSet = new Set(hiddenWitnessIds)

  return orderedWitnessIds.filter(id => orderedSet.has(id) && hiddenSet.has(id) && !lockedSet.has(id))
}

export const buildVisibleWitnessIds = (
  orderedWitnessIds: string[],
  hiddenWitnessIds: string[]
): string[] => {
  const hiddenSet = new Set(hiddenWitnessIds)
  return orderedWitnessIds.filter(id => !hiddenSet.has(id))
}

export const parseStoredWitnessOrder = (raw: string): string[] => {
  if (!raw) {
    return []
  }
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
      : []
  } catch {
    return []
  }
}

export const sanitizeWitnessOrder = (
  baseWitnessIds: string[],
  preferredWitnessIds: string[]
): string[] => {
  const baseSet = new Set(baseWitnessIds)
  const preferred = uniqueIds(preferredWitnessIds).filter(id => baseSet.has(id))
  const preferredSet = new Set(preferred)
  return [
    ...preferred,
    ...baseWitnessIds.filter(id => !preferredSet.has(id))
  ]
}

export type WitnessColumnDropPlacement = 'before' | 'after'

export const moveWitnessIdInOrder = (
  orderedWitnessIds: string[],
  draggedWitnessId: string,
  targetWitnessId: string,
  placement: WitnessColumnDropPlacement = 'before'
): string[] => {
  if (!draggedWitnessId || !targetWitnessId || draggedWitnessId === targetWitnessId) {
    return orderedWitnessIds.slice()
  }
  if (!orderedWitnessIds.includes(draggedWitnessId) || !orderedWitnessIds.includes(targetWitnessId)) {
    return orderedWitnessIds.slice()
  }
  const withoutDragged = orderedWitnessIds.filter(id => id !== draggedWitnessId)
  const targetIndex = withoutDragged.indexOf(targetWitnessId)
  if (targetIndex < 0) {
    return orderedWitnessIds.slice()
  }
  const insertIndex = placement === 'after' ? targetIndex + 1 : targetIndex
  return [
    ...withoutDragged.slice(0, insertIndex),
    draggedWitnessId,
    ...withoutDragged.slice(insertIndex)
  ]
}

export const toggleWitnessVisibility = (
  hiddenWitnessIds: string[],
  witnessId: string,
  lockVisibleWitnessIds: string[] = []
): string[] => {
  if (!witnessId) {
    return uniqueIds(hiddenWitnessIds)
  }
  if (lockVisibleWitnessIds.includes(witnessId)) {
    return uniqueIds(hiddenWitnessIds)
  }
  if (hiddenWitnessIds.includes(witnessId)) {
    return hiddenWitnessIds.filter(id => id !== witnessId)
  }
  return uniqueIds([...hiddenWitnessIds, witnessId])
}
